import { useLogger } from '@booksy-com/vue-tools'
import { useAnalytics } from '@/composables/analytics'
import type { ChatOpenOptions } from '@/composables/chat/types'
import {
  REFERRAL_SCREEN_NAMES_MAP,
  DEFAULT_SCREEN_NAME,
} from '@/composables/chat/providers/zowie/consts'
import type { AgentforceSessionData } from '@/composables/chat/providers/agentforce/types'
import { useCore } from '@/composables/core'
import { getExternalIdForAgentforcePrechat } from '@/lib/analytics/helpers'
import {
  getBooksyHiddenPrechatFields,
  mapAppLocaleToSalesforceLanguage,
  startEmbeddedMessagingSession,
  teardownEmbeddedMessagingBootstrap,
  type EmbeddedMessagingHandles,
} from '@/composables/chat/providers/agentforce/embedded-messaging-bootstrap'

export type { AgentforceSessionData } from '@/composables/chat/providers/agentforce/types'

const logger = useLogger('chat-provider-agentforce')

let sessionData: AgentforceSessionData | null = null
let sessionChanged = false
let embedResult: EmbeddedMessagingHandles | null = null
let isOpened = false
let referral: string | null = null
let embedPromise: Promise<void> | null = null
let didLogLightningOutOriginHint = false

type AgentforceBootstrapDeps = {
  apiCall: (...args: unknown[]) => Promise<unknown>
  businessId: number
}

let agentforceBootstrapDeps: AgentforceBootstrapDeps | null = null
let agentforceJwtLoadPromise: Promise<void> | null = null

/**
 * Registers `apiCall` + `businessId` for lazy JWT fetch on first {@link useAgentforce} `open`.
 * Called from {@link setupAgentforceChat}; does not POST until the user opens chat.
 */
export function setAgentforceChatBootstrapDeps (deps: AgentforceBootstrapDeps | null): void {
  agentforceBootstrapDeps = deps
}

type AgentforceEmbedSessionContextRootState = {
  app: { country: string; locale?: string; webView: string }
  routingConfig: { config: { country?: { code?: string } } & Record<string, unknown> }
  identity?: { user?: { id?: string | number } }
  business?: { business?: { id?: string | number } }
}

/** Language, IDs, device, and country for Embedded Messaging — aligned with analytics ({@link getBaseProperties}). */
function getEmbeddedMessagingSessionContextFromApp (): {
  language: string
  businessProviderId: string
  userId: string
  deviceType: string
  appCountry: string
  } {
  try {
    const { store } = useCore()
    const state = store.state as AgentforceEmbedSessionContextRootState
    const { country, locale } = state.app
    const routingConfig = state.routingConfig.config
    const deviceType = 'Web'
    const routingCountryCode = routingConfig?.country?.code
    /** Salesforce: ISO 3166-1 alpha-2 uppercase (PL, US, …). */
    const appCountry = String(routingCountryCode || country || '').toUpperCase()

    return {
      language: mapAppLocaleToSalesforceLanguage(locale),
      businessProviderId: getExternalIdForAgentforcePrechat({
        id: state.business?.business?.id,
        country,
      }) ?? '',
      userId: getExternalIdForAgentforcePrechat({
        id: state.identity?.user?.id,
        country,
      }) ?? '',
      deviceType,
      appCountry,
    }
  } catch {
    return {
      language: 'en-US',
      businessProviderId: '',
      userId: '',
      deviceType: 'Web',
      appCountry: '',
    }
  }
}

function logLightningOutOriginHintOnce (): void {
  if (didLogLightningOutOriginHint || typeof window === 'undefined') {
    return
  }
  if (!window.location.hostname.includes('localhost')) {
    return
  }
  didLogLightningOutOriginHint = true
  logger.info(
    'Agentforce / Embedded Messaging: if assets fail to load, allow this origin in Salesforce CSP / Trusted Sites: '
      + window.location.origin
  )
}

function dispatchAnalyticsEvent (params: { screenName?: string }): void {
  const screenName = params.screenName || DEFAULT_SCREEN_NAME
  const { dispatch, EVENTS } = useAnalytics()

  dispatch(EVENTS.CS_CHAT_CLICKED, {
    screenName,
  })
}

function tearDownEmbed (): void {
  teardownEmbeddedMessagingBootstrap()
  embedResult = null
}

async function ensureEmbed (): Promise<void> {
  if (!sessionData) {
    throw new Error('Agentforce data is not initialized')
  }
  if (embedResult) {
    return
  }
  if (embedPromise) {
    await embedPromise
    return
  }

  embedPromise = (async (): Promise<void> => {
    if (embedResult) {
      return
    }

    const siteBaseUrl = sessionData.embedded_messaging_site_url?.trim()
    const deploymentName = sessionData.embedded_messaging_deployment_name?.trim()
    const organizationId = sessionData.organizationId?.trim()
    const scrt2URL = sessionData.scrtBaseUrl?.trim()
    if (!organizationId || !scrt2URL) {
      throw new Error('Agentforce session is missing organization_id or url_web from the API')
    }
    if (!siteBaseUrl || !deploymentName) {
      throw new Error(
        'Agentforce session is missing url_web (embedded site URL) or embedded_messaging_deployment_name from the API'
      )
    }
    const ctx = getEmbeddedMessagingSessionContextFromApp()

    embedResult = await startEmbeddedMessagingSession({
      siteBaseUrl,
      organizationId,
      deploymentDeveloperName: deploymentName,
      scrt2URL,
      jwt: sessionData.jwt,
      language: ctx.language,
      endUserLanguage: ctx.language,
      businessProviderId: ctx.businessProviderId,
      userId: ctx.userId,
      deviceType: ctx.deviceType,
      appCountry: ctx.appCountry,
    })
    logLightningOutOriginHintOnce()
    sessionChanged = false
  })().finally(() => {
    embedPromise = null
  })

  await embedPromise
}

async function ensureJwtSession (): Promise<void> {
  if (sessionData) {
    return
  }
  const bootstrapDeps = agentforceBootstrapDeps
  if (!bootstrapDeps) {
    throw new Error('Agentforce chat is not configured (missing bootstrap deps)')
  }
  if (agentforceJwtLoadPromise) {
    await agentforceJwtLoadPromise
    return
  }

  agentforceJwtLoadPromise = (async (): Promise<void> => {
    const { getJwt } = await import('@/composables/chat/providers/agentforce/setup-agentforce-chat')
    const data = await getJwt(bootstrapDeps)
    createAgentforce({ data })
  })()

  try {
    await agentforceJwtLoadPromise
  } finally {
    agentforceJwtLoadPromise = null
  }
}

function maximize (): void {
  embedResult?.launchChat()
}

function minimize (): void {
  embedResult?.hideEmbeddedMessaging()
}

function embedThenOpen (): void {
  void (async () => {
    try {
      await ensureEmbed()
      maximize()
      isOpened = true
    } catch (e) {
      logger.error(e)
    }
  })()
}

export const createAgentforce = ({ data }: { data: AgentforceSessionData }): void => {
  if (sessionData) {
    sessionChanged = data.frontdoor_url !== sessionData.frontdoor_url
      || data.salesforce_origin !== sessionData.salesforce_origin
      || data.agent_id !== sessionData.agent_id
      || (data.app_id ?? '') !== (sessionData.app_id ?? '')
      || (data.agent_label ?? '') !== (sessionData.agent_label ?? '')
      || (data.jwt ?? '') !== (sessionData.jwt ?? '')
      || data.conversation_id !== sessionData.conversation_id
      || (data.embedded_messaging_site_url ?? '') !== (sessionData.embedded_messaging_site_url ?? '')
      || (data.embedded_messaging_deployment_name ?? '')
        !== (sessionData.embedded_messaging_deployment_name ?? '')
      || (data.organizationId ?? '') !== (sessionData.organizationId ?? '')
      || (data.scrtBaseUrl ?? '') !== (sessionData.scrtBaseUrl ?? '')
  }
  sessionData = data
}

/** Clears Agentforce session, Embedded Messaging bootstrap DOM, and module state. Call before `createChat` again for a fresh JWT/embed. */
export function resetAgentforceChat (): void {
  tearDownEmbed()
  sessionData = null
  sessionChanged = false
  isOpened = false
  referral = null
  embedPromise = null
  didLogLightningOutOriginHint = false
  agentforceBootstrapDeps = null
  agentforceJwtLoadPromise = null
}

/** @see resetAgentforceChat — kept for unit tests that mock by name. */
export function resetAgentforceChatStateForTests (): void {
  resetAgentforceChat()
}

export const useAgentforce = (): {
  open: (options?: ChatOpenOptions) => void
  close: () => void
  toggle: () => void
  logout: () => void
} => {
  const proceedAfterSessionReady = (): void => {
    if (!sessionData) {
      logger.error('Agentforce data is not initialized')
      return
    }

    if (sessionChanged && embedResult) {
      tearDownEmbed()
    }

    if (!embedResult) {
      embedThenOpen()
      return
    }

    maximize()
    isOpened = true
  }

  const open = (options?: ChatOpenOptions): void => {
    referral = options?.referral ?? null

    const screenName = referral
      ? REFERRAL_SCREEN_NAMES_MAP[referral as keyof typeof REFERRAL_SCREEN_NAMES_MAP]
      : undefined

    dispatchAnalyticsEvent({ screenName })

    if (!sessionData) {
      void ensureJwtSession()
        .then(() => {
          proceedAfterSessionReady()
        })
        .catch((e: unknown) => {
          logger.error(e)
        })
      return
    }

    proceedAfterSessionReady()
  }

  const close = (): void => {
    if (embedResult) {
      minimize()
    }
    isOpened = false
  }

  const logout = (): void => {
    close()
    resetAgentforceChat()
  }

  const toggle = (): void => {
    isOpened ? close() : open()
  }

  return {
    open,
    close,
    toggle,
    logout,
  }
}
